'use client';
import { Icon } from '@/components/ui/Icon';
import { Check, Link } from 'lucide-react';
import { useState } from 'react';

interface SocialShareProps {
  url: string;
  title: string;
}

export const SocialShare = ({ url, title }: SocialShareProps) => {
  const [copied, setCopied] = useState(false);
  const encodedUrl = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title);

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const shareLinks = [
    {
      name: 'Twitter',
      url: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`,
      icon: (
        <svg
          width="16"
          height="14"
          viewBox="0 0 16 14"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M12.6009 0H15.0544L9.69434 5.93026L16 14H11.0627L7.19566 9.10574L2.77087 14H0.31595L6.04904 7.65692L0 0H5.06262L8.55811 4.47354L12.6009 0ZM11.7399 12.5785H13.0993L4.32392 1.34687H2.86506L11.7399 12.5785Z"
            fill="#9A9A9A"
          />
        </svg>
      ),
    },
    {
      name: 'LinkedIn',
      icon: (
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M3.60353 15.9907H0.240754V5.33073H3.60353V15.9907ZM1.91981 3.85349C1.53836 3.85135 1.1661 3.73649 0.85 3.52341C0.5339 3.31032 0.288134 3.00857 0.143716 2.65622C-0.000700799 2.30388 -0.0372958 1.91674 0.0385494 1.54366C0.114395 1.17058 0.299281 0.828285 0.569876 0.559969C0.84047 0.291653 1.18464 0.109344 1.55896 0.0360503C1.93328 -0.0372439 2.32097 0.00176203 2.67309 0.148145C3.02522 0.294528 3.32601 0.541727 3.53749 0.858545C3.74897 1.17536 3.86167 1.5476 3.86137 1.92828C3.86497 2.18315 3.81712 2.43613 3.72066 2.67214C3.6242 2.90816 3.48112 3.12238 3.29994 3.30203C3.11876 3.48169 2.90319 3.6231 2.66608 3.71785C2.42897 3.81259 2.17517 3.85872 1.91981 3.85349ZM15.9984 16H12.6372V10.1763C12.6372 8.45881 11.9056 7.92868 10.9613 7.92868C9.96408 7.92868 8.98554 8.67892 8.98554 10.2197V16H5.62275V5.33848H8.85662V6.81572H8.90011C9.22474 6.16003 10.3617 5.03931 12.0967 5.03931C13.973 5.03931 16 6.15073 16 9.40592L15.9984 16Z"
            fill="#9A9A9A"
          />
        </svg>
      ),
      url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
    },
    {
      name: 'Facebook',
      icon: (
        <svg
          width="9"
          height="16"
          viewBox="0 0 9 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M8.28616 9.18297L8.8401 6.18284H5.61597V5.12181C5.61597 3.53653 6.24137 2.92669 7.85975 2.92669C8.36236 2.92669 8.76705 2.93886 9 2.96321V0.243858C8.55859 0.121733 7.47954 0 6.85413 0C3.55578 0 2.03532 1.54875 2.03532 4.89012V6.18284H0V9.18297H2.03532V15.711C2.7989 15.8995 3.59763 16 4.41965 16C4.82435 16 5.22351 15.9753 5.61557 15.9281V9.18297H8.28576H8.28616Z"
            fill="#9A9A9A"
          />
        </svg>
      ),
      url: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
    },
  ];

  return (
    <div>
      <h3 className="text-sm font-semibold mb-3">Share article</h3>
      <div className="flex gap-1 -ml-2">
        <button
          onClick={handleCopyLink}
          className="items-center hover:bg-accent rounded-md p-2"
          aria-label="Copy link to clipboard"
        >
          <Icon
            as={copied ? Check : Link}
            name={copied ? 'check' : 'link'}
            className="w-4 h-4 text-[#9A9A9A]"
            strokeWidth={2}
          />
        </button>
        {shareLinks.map((platform) => (
          <a
            key={platform.name}
            href={platform.url}
            target="_blank"
            rel="noopener noreferrer"
            className="p-2 rounded-md hover:bg-accent transition-colors"
            aria-label={`Share on ${platform.name}`}
          >
            {platform.icon}
          </a>
        ))}
      </div>
    </div>
  );
};
